import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Legend, LabelList } from 'recharts';
import { reportingFilterComparisonConst } from '../../../../../constants/reportingFilterComparisonConst';
import { reportingFilterDateRangesConst } from '../../../../../constants/reportingFilterDateRangesConst';
import typeOfContract from '../../../../../constants/typeOfContract';
import { processedCostByServiceFilter } from '../../Services/reportServices';

const CostPerServiceFiterGraph = ({
  trackReport,
  trackReportLoading,
  trackReportError,
  projectCost,
  selectedDateRange,
  selectedComparison,
  selectedFrequency,
  selectedYearRange,
  selectedMonthRange,
  selectedComparisonYear,
  selectedComparisonMonth,
}) => {
  const reportingServices = useSelector((state) => state.reportingServices);
  const { forex } = useSelector((state) => state.forex);
  const settings = JSON.parse(localStorage.getItem("settings"));

  const processedData = useMemo(() => {
    return processedCostByServiceFilter(
      trackReport,
      projectCost,
      reportingServices,
      settings.currency,
      forex,
      typeOfContract,
      selectedDateRange,
      selectedComparison,
      selectedYearRange,
      selectedMonthRange,
      selectedComparisonYear,
      selectedComparisonMonth,
    );
  }, [
    trackReport,
    projectCost,
    reportingServices,
    settings.currency,
    forex,
    selectedDateRange,
    selectedComparison,
    selectedYearRange,
    selectedMonthRange,
    selectedComparisonYear,
    selectedComparisonMonth,
  ]);

  if (trackReportLoading) {
    const skeletonRows = 8;
    const rowHeight = 50;
    const baseHeight = 100;
    const skeletonHeight = skeletonRows * rowHeight + baseHeight;
    
    // Fixed widths for skeleton bars to avoid random values
    const barWidths = [45, 60, 35, 55, 40, 50, 38, 48];
    
    return (
      <div className='graph-project-per-hour' style={{ width: '100%', height: Math.max(542, skeletonHeight) }}>
        <div style={{ 
          width: '100%', 
          height: '100%', 
          padding: '20px 42px 5px 30px',
          position: 'relative'
        }}>
          {/* Y-axis skeleton labels */}
          <div style={{ 
            position: 'absolute', 
            left: '0', 
            top: '20px', 
            bottom: '5px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingRight: '10px'
          }}>
            {Array.from({ length: skeletonRows }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '120px',
                  height: '13px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>

          {/* Chart area with bars */}
          <div style={{ 
            marginLeft: '150px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingTop: '10px',
            paddingBottom: '30px'
          }}>
            {Array.from({ length: skeletonRows }).map((_, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  height: '50px',
                  position: 'relative'
                }}
              >
                {/* First bar skeleton */}
                <div
                  style={{
                    width: `${barWidths[index] || 45}%`,
                    height: '13px',
                    backgroundColor: '#87ABE2',
                    opacity: 0.3,
                    borderRadius: '2px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
                {/* Second bar skeleton */}
                <div
                  style={{
                    width: `${(barWidths[index] || 45) - 10}%`,
                    height: '13px',
                    backgroundColor: '#C7B6F6',
                    opacity: 0.3,
                    borderRadius: '2px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: '0.2s'
                  }}
                />
              </div>
            ))}
          </div>

          {/* X-axis skeleton ticks */}
          <div style={{ 
            position: 'absolute', 
            bottom: '5px', 
            left: '150px', 
            right: '42px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end'
          }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '40px',
                  height: '12px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }
  if (trackReportError) return <div>Error loading data.</div>;

  const calculateXTicks = () => {
    if (!processedData.length) return [];

    const maxPositiveValue = Math.max(
      ...processedData.map(item => Math.max(item.currentPeriodCost, item.comparisonPeriodCost))
    );

    if (maxPositiveValue === 0) {
      return [0];
    }

    const desiredTickCount = 10;
    const tickInterval = Math.ceil(maxPositiveValue / desiredTickCount);

    const endTick = Math.ceil(maxPositiveValue / tickInterval) * tickInterval + tickInterval;

    const startTick = 0;

    const ticks = [];
    for (let i = startTick; i <= endTick; i += tickInterval) {
      ticks.push(i);
    }

    return ticks;
  };

  const xAxisTicks = calculateXTicks();

  const getBarName = (isCurrent, selectedRange, selectedComparison, selectedYearRange, selectedMonthRange, selectedComparisonYear, selectedComparisonMonth) => {
    const rangeNames = {
      [reportingFilterDateRangesConst.This_Year]: 'This Year',
      [reportingFilterDateRangesConst.This_Quarter]: 'This Quarter',
      [reportingFilterDateRangesConst.First_Half]: 'First Half',
      [reportingFilterDateRangesConst.This_Month]: 'This Month',
      [reportingFilterDateRangesConst.Year_Selected]: selectedYearRange ? `Year ${selectedYearRange.getFullYear()}` : `Selected Year`,
      [reportingFilterDateRangesConst.Month_Selected]: selectedMonthRange ? `Month ${selectedMonthRange.toLocaleString('default', { month: 'long' })} ${selectedMonthRange.getFullYear()}` : `Selected Month`,
    };

    const comparisonNames = {
      [reportingFilterComparisonConst.Last_Year]: 'Last Year',
      [reportingFilterComparisonConst.Last_Quarter]: 'Last Quarter',
      [reportingFilterComparisonConst.Second_Half]: 'Second Half',
      [reportingFilterComparisonConst.Last_Month]: 'Last Month',
      [reportingFilterComparisonConst.Year_Select]: selectedComparisonYear ? `Year ${selectedComparisonYear.getFullYear()}` : `Previous Year`,
      [reportingFilterComparisonConst.Month_Select]: selectedComparisonMonth ? `Month ${selectedComparisonMonth.toLocaleString('default', { month: 'long' })} ${selectedComparisonMonth.getFullYear()}` : `Previous Month`,
    };

    const rangeName = rangeNames[selectedRange] || 'Current Range';
    const comparisonName = comparisonNames[selectedComparison] || 'Comparison Range';

    return isCurrent
      ? `${rangeName}`
      : `${comparisonName}`;
  };

  const calculateChartHeight = (dataLength) => {
    const rowHeight = 50;
    const baseHeight = 100;
    return dataLength * rowHeight + baseHeight;
  };

  const calculateLeftMargin = (data) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = '12px Arial';

    const longestWordLength = data.reduce((maxLength, item) => {
      const firstWord = item.name.split(' ')[0];
      const wordWidth = context.measureText(firstWord).width;
      return Math.max(maxLength, wordWidth);
    }, 0);

    return Math.max(30, longestWordLength - 150);
  };

  const leftMargin = calculateLeftMargin(processedData);

  return (
    <div className='graph-project-per-hour' style={{ width: '100%', height: Math.max(542, calculateChartHeight(processedData.length)), }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={processedData}
          barSize={13}
          margin={{
            top: 20,
            right: 42,
            left: leftMargin,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
          <XAxis
            type="number"
            domain={[Math.min(...xAxisTicks), Math.max(...xAxisTicks)]}
            tickFormatter={(tick) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: settings.currency, }).format(tick)}`}
            ticks={xAxisTicks}
          />
          <YAxis
            type="category"
            dataKey="name"
            // width={150} // Ensure enough width for expense categories
            tickMargin={10}
          />
          <Tooltip formatter={(value) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: settings.currency, }).format(value)}`} />
          <ReferenceLine x={0} stroke="#7393B3" />
          <Bar dataKey="currentPeriodCost" name={getBarName(true, selectedDateRange, selectedComparison, selectedYearRange, selectedMonthRange, selectedComparisonYear, selectedComparisonMonth)} fill="#87ABE2">
            <LabelList dataKey="currentPeriodCost" position="right" formatter={(value) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: settings.currency, }).format(value)}`} />
          </Bar>
          <Bar dataKey="comparisonPeriodCost" name={getBarName(false, selectedDateRange, selectedComparison, selectedYearRange, selectedMonthRange, selectedComparisonYear, selectedComparisonMonth)} fill="#C7B6F6">
            <LabelList dataKey="comparisonPeriodCost" position="right" formatter={(value) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: settings.currency, }).format(value)}`} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default CostPerServiceFiterGraph;